
function nowMs() {
  return (typeof performance !== 'undefined' && typeof performance.now === 'function') ? performance.now() : Date.now();
}

function debugEnabled() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('sff_3d_debug') === '1' || localStorage.getItem('sff-3d-debug') === '1';
  } catch (_) {
    return false;
  }
}

export function createWorkbenchRuntime({
  getScene,
  getCamera,
  getRenderer,
  getControls,
  getViewport,
  getHeadLight,
  getActiveBoxes,
  onSpatialVisuals,
  onBadges,
  getExtraStats = null
} = {}) {
  const perf = {
    animationTicks: 0,
    renderedFrames: 0,
    skippedFrames: 0,
    conflictPasses: 0,
    conflictMsTotal: 0,
    lastConflictMs: 0,
    solverRuns: 0,
    solverMsTotal: 0,
    lastSolverMs: 0,
    catalogLoadMs: 0,
    disposedGeometries: 0,
    disposedMaterials: 0,
    disposedTextures: 0
  };

  let renderDirty = true;
  let spatialVisualDirty = true;
  let cameraVisualDirty = true;
  let shadowDirty = true;
  let animationStarted = false;
  let resizeFrameRequest = 0;
  let viewportResizeObserver = null;
  let lastCanvasViewportWidth = 0;
  let lastCanvasViewportHeight = 0;
  let lastActiveObjectRefs = [];

  const objectTransforms = new WeakMap();
  const cameraTransform = {
    px:NaN,py:NaN,pz:NaN,qx:NaN,qy:NaN,qz:NaN,qw:NaN,
    tx:NaN,ty:NaN,tz:NaN,aspect:NaN,fov:NaN,zoom:NaN
  };

  function markRenderDirty() {
    renderDirty = true;
  }

  function markSpatialDirty() {
    renderDirty = true;
    spatialVisualDirty = true;
    cameraVisualDirty = true;
    shadowDirty = true;
  }

  function markCameraDirty() {
    renderDirty = true;
    cameraVisualDirty = true;
  }

  function markShadowDirty() {
    renderDirty = true;
    shadowDirty = true;
  }

  function activeObjectsChanged() {
    const activeBoxes = getActiveBoxes?.() || [];
    let changed = lastActiveObjectRefs.length !== activeBoxes.length;
    if (!changed) {
      for (let i = 0; i < activeBoxes.length; i++) {
        if (lastActiveObjectRefs[i] !== activeBoxes[i]) {
          changed = true;
          break;
        }
      }
    }
    if (changed) lastActiveObjectRefs = activeBoxes.slice();

    for (const obj of activeBoxes) {
      if (!obj) continue;
      const p=obj.position, q=obj.quaternion, sc=obj.scale;
      const previous=objectTransforms.get(obj);
      const parent=obj.parent || null;
      if (!previous
        || previous.parent!==parent
        || previous.visible!==obj.visible
        || previous.px!==p.x || previous.py!==p.y || previous.pz!==p.z
        || previous.qx!==q.x || previous.qy!==q.y || previous.qz!==q.z || previous.qw!==q.w
        || previous.sx!==sc.x || previous.sy!==sc.y || previous.sz!==sc.z) {
        objectTransforms.set(obj,{
          parent,visible:obj.visible,
          px:p.x,py:p.y,pz:p.z,
          qx:q.x,qy:q.y,qz:q.z,qw:q.w,
          sx:sc.x,sy:sc.y,sz:sc.z
        });
        changed=true;
      }
    }
    return changed;
  }

  function activeCameraChanged() {
    const camera=getCamera?.();
    if (!camera) return false;
    const controls=getControls?.();
    const p=camera.position,q=camera.quaternion,target=controls?.target;
    const tx=target?.x ?? 0, ty=target?.y ?? 0, tz=target?.z ?? 0;
    const changed=
      cameraTransform.px!==p.x || cameraTransform.py!==p.y || cameraTransform.pz!==p.z
      || cameraTransform.qx!==q.x || cameraTransform.qy!==q.y || cameraTransform.qz!==q.z || cameraTransform.qw!==q.w
      || cameraTransform.tx!==tx || cameraTransform.ty!==ty || cameraTransform.tz!==tz
      || cameraTransform.aspect!==camera.aspect || cameraTransform.fov!==camera.fov || cameraTransform.zoom!==camera.zoom;
    if (changed) {
      cameraTransform.px=p.x; cameraTransform.py=p.y; cameraTransform.pz=p.z;
      cameraTransform.qx=q.x; cameraTransform.qy=q.y; cameraTransform.qz=q.z; cameraTransform.qw=q.w;
      cameraTransform.tx=tx; cameraTransform.ty=ty; cameraTransform.tz=tz;
      cameraTransform.aspect=camera.aspect; cameraTransform.fov=camera.fov; cameraTransform.zoom=camera.zoom;
    }
    return changed;
  }

  function disposeMaterialTextures(material, textureSet) {
    if (!material || typeof material !== 'object') return;
    for (const value of Object.values(material)) {
      if (value?.isTexture && !textureSet.has(value)) {
        textureSet.add(value);
        value.dispose?.();
        perf.disposedTextures++;
      }
    }
  }

  function disposeObject3DResources(rootObject) {
    if (!rootObject) return;
    const geometries = new Set();
    const materials = new Set();
    const textures = new Set();
    const visit = typeof rootObject.traverse === 'function'
      ? callback => rootObject.traverse(callback)
      : callback => callback(rootObject);

    visit(node => {
      if (node?.geometry && !geometries.has(node.geometry)) {
        geometries.add(node.geometry);
        node.geometry.dispose?.();
        perf.disposedGeometries++;
      }
      const nodeMaterials = Array.isArray(node?.material) ? node.material : (node?.material ? [node.material] : []);
      for (const material of nodeMaterials) {
        if (!material || materials.has(material)) continue;
        materials.add(material);
        disposeMaterialTextures(material, textures);
        material.dispose?.();
        perf.disposedMaterials++;
      }
    });
  }

  function recordSolver(ms, reason = '') {
    const value = Number(ms) || 0;
    perf.solverRuns++;
    perf.lastSolverMs = value;
    perf.solverMsTotal += value;
    if (debugEnabled()) console.info(`[SFF 3D PERF] spatial-solve ${value.toFixed(2)}ms${reason ? ` · ${reason}` : ''}`);
  }

  function recordConflict(ms) {
    const value = Number(ms) || 0;
    perf.conflictPasses++;
    perf.lastConflictMs = value;
    perf.conflictMsTotal += value;
  }

  function setCatalogLoadMs(ms) {
    perf.catalogLoadMs = Number(ms) || 0;
    if (debugEnabled()) console.info(`[SFF 3D PERF] catalog ${perf.catalogLoadMs.toFixed(2)}ms`);
  }

  function renderNow({ updateBadges = true } = {}) {
    const renderer=getRenderer?.();
    const scene=getScene?.();
    const camera=getCamera?.();
    if (!renderer || !scene || !camera) return false;

    try {
      if (updateBadges) onBadges?.();
      const headLight=getHeadLight?.();
      if (headLight) headLight.position.copy(camera.position);
      if (shadowDirty && renderer.shadowMap?.enabled) renderer.shadowMap.needsUpdate = true;
      renderer.render(scene, camera);
      shadowDirty = false;
      renderDirty = false;
      cameraVisualDirty = false;
      perf.renderedFrames++;
      return true;
    } catch (_) {
      markCameraDirty();
      return false;
    }
  }

  function scheduleResize() {
    if (resizeFrameRequest) return;
    resizeFrameRequest = requestAnimationFrame(() => {
      resizeFrameRequest = 0;
      resizeNow();
    });
  }

  function resizeNow() {
    const viewport=getViewport?.();
    const camera=getCamera?.();
    const renderer=getRenderer?.();
    if (!viewport || !camera || !renderer) return false;
    const width = Math.max(1, viewport.clientWidth);
    const height = Math.max(1, viewport.clientHeight);
    if (width === lastCanvasViewportWidth && height === lastCanvasViewportHeight) return false;

    lastCanvasViewportWidth = width;
    lastCanvasViewportHeight = height;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);

    // setSize() reallocates/clears the backing buffer, so repaint immediately rather than
    // waiting for a later demand-render frame.
    activeCameraChanged();
    renderNow({ updateBadges:true });
    return true;
  }

  function installResizeHandling() {
    window.addEventListener('resize', scheduleResize);
    const viewport=getViewport?.();
    if (viewport && typeof ResizeObserver !== 'undefined') {
      viewportResizeObserver = new ResizeObserver(scheduleResize);
      viewportResizeObserver.observe(viewport);
    }
    resizeNow();
  }

  function startRenderLoop() {
    if (animationStarted) return;
    animationStarted = true;

    function frame() {
      requestAnimationFrame(frame);
      perf.animationTicks++;

      const controls=getControls?.();
      try { controls?.update?.(); } catch (_) {}

      const objectsChanged = activeObjectsChanged();
      const cameraChanged = activeCameraChanged();

      if (objectsChanged) {
        spatialVisualDirty = true;
        cameraVisualDirty = true;
        renderDirty = true;
        shadowDirty = true;
      }
      if (cameraChanged) {
        cameraVisualDirty = true;
        renderDirty = true;
      }

      if (!renderDirty && !spatialVisualDirty && !cameraVisualDirty) {
        perf.skippedFrames++;
        return;
      }

      const updateSpatialVisuals = spatialVisualDirty;
      const updateBadges = spatialVisualDirty || cameraVisualDirty;

      try { if (updateSpatialVisuals) onSpatialVisuals?.(); } catch (_) {}
      try { if (updateBadges) onBadges?.(); } catch (_) {}
      try {
        const camera=getCamera?.();
        const headLight=getHeadLight?.();
        if (camera && headLight && cameraChanged) headLight.position.copy(camera.position);
      } catch (_) {}

      spatialVisualDirty = false;
      cameraVisualDirty = false;

      const renderer=getRenderer?.();
      const scene=getScene?.();
      const camera=getCamera?.();
      try {
        if (shadowDirty && renderer?.shadowMap?.enabled) renderer.shadowMap.needsUpdate = true;
        renderer?.render?.(scene, camera);
        shadowDirty = false;
        perf.renderedFrames++;
        renderDirty = false;
      } catch (_) {}
    }

    requestAnimationFrame(frame);
  }

  function getStats() {
    const rendererInfo = getRenderer?.()?.info;
    const activeBoxes=getActiveBoxes?.() || [];
    return {
      ...perf,
      renderEfficiency: perf.animationTicks ? perf.renderedFrames / perf.animationTicks : 0,
      averageConflictMs: perf.conflictPasses ? perf.conflictMsTotal / perf.conflictPasses : 0,
      averageSolverMs: perf.solverRuns ? perf.solverMsTotal / perf.solverRuns : 0,
      renderer: rendererInfo ? {
        memory: { ...(rendererInfo.memory || {}) },
        render: { ...(rendererInfo.render || {}) }
      } : null,
      activeObjects: activeBoxes.length,
      activeCases: activeBoxes.filter(item => item?.isCase).length,
      activeComponents: activeBoxes.filter(item => item?.isComponent).length,
      ...(typeof getExtraStats === 'function' ? getExtraStats() : {})
    };
  }

  return {
    now: nowMs,
    debugEnabled,
    markRenderDirty,
    markSpatialDirty,
    markCameraDirty,
    markShadowDirty,
    disposeObject3DResources,
    recordSolver,
    recordConflict,
    setCatalogLoadMs,
    renderNow,
    scheduleResize,
    resizeNow,
    installResizeHandling,
    startRenderLoop,
    getStats
  };
}
